// ─── Activity Log Types ───────────────────────────────────────────────────────
// Two-tier logging model:
//   Tier 1 — Platform events  : auto-fired by useActivityLog, no trait mutation
//   Tier 2 — Life events      : fired by useTraitMutation emitters, mutates traits
//             (emitters internally call logEvent so there is ONE call site per action)

export type EventCategory = 'platform' | 'self_reported' | 'external'

// ─── Tier 1 — Platform event types ──────────────────────────────────────────
export type PlatformEventType =
  | 'lesson_started'       // meta: { topicId, pathId }
  | 'lesson_completed'     // meta: { topicId, pathId, timeSpentSeconds, quizScore? }
  | 'lesson_revisited'     // meta: { topicId, attemptNumber }
  | 'resource_opened'      // meta: { resourceId, resourceType: 'video'|'pdf'|'notes'|'link', topicId }
  | 'quiz_attempted'       // meta: { topicId, score, correct, total, timeTakenSeconds }
  | 'game_played'          // meta: { gameId, gameName, score, maxScore, durationSeconds }
  | 'goal_completed'       // meta: { goalId, goalType, daysToComplete }
  | 'path_enrolled'        // meta: { pathId, pathName }
  | 'ai_chat_session'      // meta: { messageCount, topicsDiscussed: string[], durationSeconds }
  | 'notes_written'        // meta: { topicId, wordCount }
  | 'session_start'        // meta: { timeOfDay: 'morning'|'afternoon'|'evening'|'night' }
  | 'session_end'          // meta: { durationSeconds, pagesVisited: number }

// ─── Tier 2 — Student-reported event types ───────────────────────────────────
export type SelfReportedEventType =
  | 'extracurricular'      // meta: { activityName, activityType, outcomeRating: 1-5, score?, note? }
  | 'study_session'        // meta: { method: 'solo'|'group', topicsRevised: string[], durationMinutes }
  | 'reading'              // meta: { title, pagesRead, comprehensionRating: 1-5 }
  | 'project'              // meta: { title, collaborators: number, outcome: string }
  | 'competition'          // meta: { name, placement?, note? }
  | 'volunteering'         // meta: { organization, hoursSpent, cause }
  | 'club'                 // meta: { clubName, role, meetingFrequency }
  | 'exam'                 // meta: { examName, subject, score, grade }

// ─── External events (exam uploads, teacher feedback) ────────────────────────
export type ExternalEventType =
  | 'exam_result_uploaded' // meta: { examType, subject, score, grade, verifiedBy? }
  | 'teacher_assessment'   // meta: { subject, score, feedback? }

export type ActivityEventType = PlatformEventType | SelfReportedEventType | ExternalEventType

// ─── Shared base fields ───────────────────────────────────────────────────────
interface ActivityLogBase {
  id: string
  userId: string
  eventCategory: EventCategory
  eventType: ActivityEventType
  timestamp: string              // ISO 8601 with time-of-day precision
  sessionId?: string             // groups events in the same browser session
  subjectArea?: string           // 'Mathematics', 'Biology', 'General'
  topicFamilyId?: string         // links to curriculum topic family
  careerPathId?: string          // links to career path
  durationSeconds?: number
  tags: string[]
  traitEventId?: string          // set only if this entry also created a TraitEvent
}

// ─── Full ActivityLogEntry ────────────────────────────────────────────────────
export interface ActivityLogEntry extends ActivityLogBase {
  meta: Record<string, unknown>  // event-specific fields (see type tables above)
  outcomeScore?: number          // 0-100, set for quiz / exam / game events
  outcomeRating?: 1 | 2 | 3 | 4 | 5  // self-reported satisfaction (1=struggled, 5=crushed it)
  sentimentLabel?: 'positive' | 'neutral' | 'negative'  // derived from outcomeScore + rating
}

// ─── Resource type helper ─────────────────────────────────────────────────────
export type ResourceType = 'video' | 'pdf' | 'notes' | 'link'
export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night'
export type StudyMethod = 'solo' | 'group'

// ─── Factory helpers ──────────────────────────────────────────────────────────
export function makeActivityId(): string {
  return `act_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

export function currentTimeOfDay(): TimeOfDay {
  const h = new Date().getHours()
  if (h >= 5 && h < 12) return 'morning'
  if (h >= 12 && h < 17) return 'afternoon'
  if (h >= 17 && h < 21) return 'evening'
  return 'night'
}

export function deriveSentiment(
  outcomeScore?: number,
  outcomeRating?: number,
): 'positive' | 'neutral' | 'negative' | undefined {
  const score = outcomeScore ?? (outcomeRating != null ? outcomeRating * 20 : undefined)
  if (score == null) return undefined
  if (score >= 70) return 'positive'
  if (score >= 40) return 'neutral'
  return 'negative'
}
